import { useState, type ComponentType } from "react";
import { useAuth } from "../auth/AuthProvider";
import { useProfiles } from "../goals/api";
import { useToast } from "../../components/Toast";
import { FlameIcon, HeartIcon, MessageCircleIcon, ThumbsUpIcon } from "../../components/icons";
import { cn } from "../../lib/cn";
import { errorMessage } from "../../lib/errors";
import { relativeTime } from "../../lib/time";
import type { ReactionKind } from "../../lib/database.types";
import {
  useAddComment,
  useComments,
  useDeleteComment,
  useLikes,
  useToggleReaction,
} from "./social";

const KINDS: { kind: ReactionKind; label: string; Icon: ComponentType<{ size?: number }> }[] = [
  { kind: "flame", label: "Flame", Icon: FlameIcon },
  { kind: "heart", label: "Heart", Icon: HeartIcon },
  { kind: "thumb", label: "Thumbs up", Icon: ThumbsUpIcon },
];

/**
 * Reactions and comments on a check-in. You can comment on anything, but only
 * react to other people's posts — on your own you just see what you got.
 */
export function Reactions({ checkInId, ownerId }: { checkInId: string; ownerId: string }) {
  const { userId } = useAuth();
  const toast = useToast();
  const profiles = useProfiles();
  const reactions = useLikes();
  const comments = useComments();
  const toggle = useToggleReaction(userId!);
  const addComment = useAddComment(userId!);
  const deleteComment = useDeleteComment();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState("");

  const mine = ownerId === userId;
  const here = (reactions.data ?? []).filter((r) => r.check_in_id === checkInId);
  const commentsHere = (comments.data ?? []).filter((comment) => comment.check_in_id === checkInId);
  const nameOf = (id: string) =>
    id === userId
      ? "you"
      : (profiles.data?.find((profile) => profile.id === id)?.display_name ?? "someone");

  function submit() {
    const body = draft.trim();
    if (!body) return;
    addComment.mutate(
      { checkInId, body },
      {
        onSuccess: () => setDraft(""),
        onError: (error) => toast(errorMessage(error, "Couldn't post that comment"), "error"),
      },
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1.5">
        {KINDS.map(({ kind, label, Icon }) => {
          const ofKind = here.filter((r) => r.kind === kind);
          const active = ofKind.some((r) => r.owner_id === userId);
          if (mine && ofKind.length === 0) return null;

          const chip = cn(
            "flex h-[30px] items-center gap-[5px] rounded-full border px-2.5 font-['Geist_Mono',ui-monospace,monospace] text-xs",
            "transition-[color,background-color,border-color,transform] duration-[120ms] motion-reduce:transition-none",
            active
              ? "border-[oklch(0.89_0.05_210)] bg-[oklch(0.96_0.03_220)] text-[oklch(0.42_0.07_220)]"
              : "border-[#ececef] text-[#56565e]",
          );

          return mine ? (
            <span key={kind} className={chip} aria-label={`${ofKind.length} ${label}`}>
              <Icon size={14} />
              <span>{ofKind.length}</span>
            </span>
          ) : (
            <button
              key={kind}
              type="button"
              aria-pressed={active}
              aria-label={label}
              onClick={() =>
                toggle.mutate(
                  { checkInId, kind, active },
                  { onError: (error) => toast(errorMessage(error, "Couldn't react"), "error") },
                )
              }
              className={cn(chip, "active:scale-[1.08] motion-reduce:active:scale-100")}
            >
              <Icon size={14} />
              <span>{ofKind.length}</span>
            </button>
          );
        })}

        <button
          type="button"
          aria-expanded={open}
          aria-label={`${commentsHere.length} comments`}
          onClick={() => setOpen((value) => !value)}
          className="ml-auto flex h-[30px] items-center gap-[5px] text-[13px] text-[#56565e]"
        >
          <MessageCircleIcon size={15} />
          <span className="tabular-nums">{commentsHere.length}</span>
        </button>
      </div>

      {open && (
        <div className="flex flex-col gap-2">
          {commentsHere.length > 0 && (
            <ul className="flex flex-col gap-1.5">
              {commentsHere.map((comment) => (
                <li key={comment.id} className="flex items-baseline gap-2 text-[13px]">
                  <span className="min-w-0 flex-1">
                    <span className="font-semibold">{nameOf(comment.owner_id)}</span>{" "}
                    <span className="text-[#56565e]">{comment.body}</span>
                  </span>
                  <span className="shrink-0 font-['Geist_Mono',ui-monospace,monospace] text-[11px] text-[#8a8a94]">
                    {relativeTime(comment.created_at).replace(/ ago$/, "")}
                  </span>
                  {comment.owner_id === userId && (
                    <button
                      type="button"
                      aria-label="Delete comment"
                      onClick={() => deleteComment.mutate(comment.id)}
                      className="shrink-0 text-[11px] text-[#8a8a94] hover:text-[#0a0a0b]"
                    >
                      delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            <label className="sr-only" htmlFor={`comment-${checkInId}`}>
              Add a comment
            </label>
            <input
              id={`comment-${checkInId}`}
              value={draft}
              maxLength={500}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="add a comment"
              className="h-9 min-w-0 flex-1 rounded-full border border-[#ececef] bg-white px-3.5 text-sm outline-none placeholder:text-[#8a8a94] focus:border-[#0a0a0b]"
            />
            <button
              type="submit"
              disabled={!draft.trim() || addComment.isPending}
              className="h-9 shrink-0 rounded-full bg-[#0a0a0b] px-4 text-[13px] font-medium text-white transition-colors active:bg-[#08080a] disabled:opacity-40"
            >
              Post
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
